import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import {
  Card,
  ErrorState,
  Loading,
  PageHeader,
  ScoreBadge,
  Select,
} from '@/components/ui';
import {
  WINDOWS,
  formatDuration,
  formatMoney,
  formatNumber,
  formatPercent,
  formatService,
} from '@/lib/format';
import { SERIES, axisProps, gridPropsVertical, tooltipProps } from '@/lib/chartTheme';
import { useShowsUsageCosts } from '@/lib/license';

interface BotRow {
  bot_id: string;
  bot_name: string;
  code: string;
  bot_group_name: string | null;
  calls: number;
  qualified: number;
  transferred: number;
  avg_seconds: string;
  avg_score: string;
  charges: string;
}

interface ServiceRow {
  service: string;
  leads: number;
  qualified: number;
  avg_score: number;
}

interface AgentRow {
  vicidial_user: string;
  calls: number;
  qualified: number;
  transfers_connected: number;
  avg_transfer_wait_sec: string;
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const showsCosts = useShowsUsageCosts();

  const bots = useQuery({
    queryKey: ['analytics-bots', days],
    queryFn: () => api.get<{ data: BotRow[] }>('/analytics/bots', { days }),
  });
  const services = useQuery({
    queryKey: ['analytics-services', days],
    queryFn: () => api.get<{ data: ServiceRow[] }>('/analytics/services', { days }),
  });
  const agents = useQuery({
    queryKey: ['analytics-agents', days],
    queryFn: () => api.get<{ data: AgentRow[] }>('/analytics/agents', { days }),
  });

  const loading = bots.isPending || services.isPending || agents.isPending;
  const error = bots.error ?? services.error ?? agents.error;

  // Unwrapped here rather than read through `loading` below: that guard is a
  // separate boolean, so TypeScript cannot use it to narrow `data`, and an
  // empty array is the correct thing to render for a query that has not
  // resolved anyway.
  const serviceRows = services.data?.data ?? [];
  const botRows = bots.data?.data ?? [];
  const agentRows = agents.data?.data ?? [];

  return (
    <div className="w-full">
      <PageHeader
        title="Analytics"
        description="Where the leads come from and which bots produce them."
        actions={
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-auto"
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                Last {w.label}
              </option>
            ))}
          </Select>
        }
      />

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={() => { bots.refetch(); services.refetch(); agents.refetch(); }} />
      ) : (
        <div className="space-y-6">
          <Card
            title="Services produced"
            description="One universal bot, every Home Services vertical. A single call can appear in more than one row."
          >
            {serviceRows.length === 0 ? (
              <p className="py-10 text-center text-sm text-ink-500">
                No services identified in this window.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(200, serviceRows.length * 32)}>
                <BarChart
                  layout="vertical"
                  data={serviceRows.map((row) => ({
                    ...row,
                    label: formatService(row.service),
                  }))}
                  margin={{ left: 16, right: 24 }}
                >
                  <CartesianGrid {...gridPropsVertical} />
                  <XAxis type="number" {...axisProps} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={150}
                    {...axisProps}
                  />
                  <Tooltip {...tooltipProps} />
                  <Bar dataKey="leads" name="Leads" fill={SERIES.primary} radius={[0, 3, 3, 0]} />
                  <Bar dataKey="qualified" name="Qualified" fill={SERIES.success} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title="Bot performance" padded={false}>
            <div className="overflow-x-auto">
              <table className="glee-table">
                <thead>
                  <tr>
                    {['Bot', 'Group', 'Calls', 'Qualified', 'Rate', 'Transferred', 'Avg length', 'Avg score', ...(showsCosts ? ['Charges'] : [])].map(
                      (header, index) => (
                        <th
                          key={header}
                          className={`${
                            index >= 2 ? 'text-right' : 'text-left'
                          }`}
                        >
                          {header}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {botRows.map((bot) => (
                    <tr key={bot.bot_id}>
                      <td>
                        <div className="font-medium text-ink-900 dark:text-ink-50">{bot.bot_name}</div>
                        <div className="font-mono text-xs text-ink-500">{bot.code}</div>
                      </td>
                      <td className="text-xs text-ink-500">
                        {bot.bot_group_name ?? '—'}
                      </td>
                      <td className="text-right tabular-nums">{formatNumber(bot.calls)}</td>
                      <td className="text-right tabular-nums">{formatNumber(bot.qualified)}</td>
                      <td className="text-right tabular-nums">
                        {bot.calls > 0 ? formatPercent((bot.qualified / bot.calls) * 100) : '—'}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatNumber(bot.transferred)}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatDuration(Number(bot.avg_seconds))}
                      </td>
                      <td className="text-right">
                        <ScoreBadge score={Math.round(Number(bot.avg_score))} />
                      </td>
                      {showsCosts && (
                        <td className="text-right tabular-nums">
                          {formatMoney(bot.charges)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card
            title="Agent performance"
            description="Calls attributed to each Vicidial user."
            padded={false}
          >
            <div className="overflow-x-auto">
              <table className="glee-table">
                <thead>
                  <tr>
                    {['Vicidial user', 'Calls', 'Qualified', 'Connected', 'Avg wait'].map(
                      (header, index) => (
                        <th
                          key={header}
                          className={`${
                            index >= 1 ? 'text-right' : 'text-left'
                          }`}
                        >
                          {header}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {agentRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-sm text-ink-500">
                        No agent activity in this window.
                      </td>
                    </tr>
                  ) : (
                    agentRows.map((agent) => (
                      <tr key={agent.vicidial_user}>
                        <td className="font-mono text-sm">{agent.vicidial_user}</td>
                        <td className="text-right tabular-nums">
                          {formatNumber(agent.calls)}
                        </td>
                        <td className="text-right tabular-nums">
                          {formatNumber(agent.qualified)}
                        </td>
                        <td className="text-right tabular-nums">
                          {formatNumber(agent.transfers_connected)}
                        </td>
                        <td className="text-right tabular-nums">
                          {Math.round(Number(agent.avg_transfer_wait_sec))}s
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
